import type { SiteConfig } from './types.ts';
import { absoluteUrl } from './url.ts';

/**
 * Dữ liệu có cấu trúc (schema.org). Đây là phần quyết định trang có được hiển thị dạng
 * rich result hay không — sao đánh giá, breadcrumb trên kết quả tìm kiếm, khối FAQ mở rộng.
 *
 * Trả về object thuần để phía React `JSON.stringify` vào `<script type="application/ld+json">`.
 * Không tự sinh chuỗi HTML ở đây để tránh mở đường cho lỗi chèn mã.
 */
export type JsonLd = Record<string, unknown>;

export interface OrganizationInput {
  readonly legalName?: string;
  readonly logo: string;
  readonly sameAs?: readonly string[];
  readonly email?: string;
  readonly telephone?: string;
}

export function organization(site: SiteConfig, input: OrganizationInput): JsonLd {
  const contact =
    input.email === undefined && input.telephone === undefined
      ? undefined
      : {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          ...(input.email === undefined ? {} : { email: input.email }),
          ...(input.telephone === undefined ? {} : { telephone: input.telephone }),
        };

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${absoluteUrl(site.url, '/')}#organization`,
    name: site.name,
    url: absoluteUrl(site.url, '/'),
    logo: absoluteUrl(site.url, input.logo),
    ...(input.legalName === undefined ? {} : { legalName: input.legalName }),
    ...(input.sameAs === undefined ? {} : { sameAs: [...input.sameAs] }),
    ...(contact === undefined ? {} : { contactPoint: contact }),
  };
}

export function webSite(site: SiteConfig, locale: string, homePath: string): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${absoluteUrl(site.url, '/')}#website`,
    name: site.name,
    url: absoluteUrl(site.url, homePath),
    inLanguage: locale,
    publisher: { '@id': `${absoluteUrl(site.url, '/')}#organization` },
  };
}

export interface BreadcrumbItem {
  readonly name: string;
  readonly path: string;
}

/**
 * Breadcrumb giúp Google hiển thị đường dẫn phân cấp thay vì URL thô trên kết quả tìm kiếm.
 * Trang chủ không cần breadcrumb (chỉ có một mắt) — gọi với mảng 1 phần tử sẽ trả về undefined
 * để phía gọi khỏi phải tự kiểm tra.
 */
export function breadcrumbList(
  site: SiteConfig,
  items: readonly BreadcrumbItem[],
): JsonLd | undefined {
  if (items.length < 2) return undefined;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(site.url, item.path),
    })),
  };
}

export interface FaqItem {
  readonly question: string;
  readonly answer: string;
}

export function faqPage(items: readonly FaqItem[]): JsonLd | undefined {
  if (items.length === 0) return undefined;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}

export interface OfferInput {
  readonly name: string;
  readonly price: string;
  readonly currency: string;
  readonly description: string;
}

export interface SoftwareInput {
  readonly description: string;
  readonly category: string;
  readonly operatingSystem: string;
  readonly offers: readonly OfferInput[];
}

export function softwareApplication(site: SiteConfig, input: SoftwareInput): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: site.name,
    description: input.description,
    applicationCategory: input.category,
    operatingSystem: input.operatingSystem,
    offers: input.offers.map((offer) => ({
      '@type': 'Offer',
      name: offer.name,
      price: offer.price,
      priceCurrency: offer.currency,
      description: offer.description,
    })),
  };
}
